import { Signal } from "signal-polyfill";

import { CONFIG_KEYS, DEFAULT_GROUP_ID } from "../config/config.js";
import { compactContext } from "../core/orchestrator/utils/compactContext.js";

import {
  flushTerminalWorkspace,
  syncTerminalWorkspace,
} from "../core/orchestrator/utils/operations/vm.js";

import {
  setGitProxyUrl,
  setProxyUrl,
  setUseProxy,
  setVMBashFullInternetAccess,
} from "../core/orchestrator/utils/settings.js";

import { clearGroupMessages } from "../db/clearGroupMessages.js";
import { cloneGroupMessages } from "../db/cloneGroupMessages.js";
import { cloneGroupTasks } from "../db/cloneGroupTasks.js";
import { deleteMessage } from "../db/deleteMessage.js";
import { deleteTask } from "../db/deleteTask.js";
import { getAllTasks } from "../db/getAllTasks.js";
import { getConfig } from "../db/getConfig.js";
import { getOrCreateSubscriberId } from "../db/getOrCreateSubscriberId.js";
import { getRecentMessages } from "../db/getRecentMessages.js";

import {
  cloneGroup,
  createGroup,
  deleteGroupMetadata,
  listGroups,
  renameGroup,
  reorderGroups,
  saveGroupMetadata,
  updateGroupPinnedProvider,
  updateGroupProviderRuntimeOverrides,
  updateGroupSubagentSettings,
  updateGroupToolTags,
} from "../db/groups.js";

import { saveMessage } from "../db/saveMessage.js";
import { saveTask } from "../db/saveTask.js";
import { setConfig } from "../db/setConfig.js";
import { updateTaskLastRun } from "../db/updateTaskLastRun.js";
import { reorderTasks } from "../db/reorderTasks.js";
import { copyGroupDirectory } from "../storage/copyGroupDirectory.js";
import { ensureMainGroupIndex } from "../storage/ensureMainGroupIndex.js";

import {
  DEFAULT_MAIN_GROUP_MEMORY_PATH,
  ensureMainGroupMemory,
  isMainGroupMemorySuppressed,
  setMainGroupMemorySuppressed,
} from "../storage/ensureMainGroupMemory.js";

import { listGroupFiles } from "../storage/listGroupFiles.js";
import { readGroupFile } from "../storage/readGroupFile.js";
import { requestStorageAccess } from "../storage/requestStorageAccess.js";
import { getStorageStatus } from "../storage/storage.js";
import {
  configureCustomElementSecurity,
  installCustomElementDomGuard,
  installCustomElementsRegistryGuard,
  loadCustomElementSecurityFromDb,
} from "../security/custom-element-security.js";
import {
  isStaticMainSiteSeeded,
  seedStaticMainSite,
} from "../storage/staticMainSite.js";
import { suppressPage, unsuppressPage } from "../storage/suppressedPages.js";
import { writeGroupFile } from "../storage/writeGroupFile.js";
import { AGUIAdapter } from "../ui/agui-adapter.js";
import { showError } from "../ui/toast.js";
import { applyJsonPatch } from "../utils/jsonPatch.js";
import { ulid } from "../utils/ulid.js";
import { toolsStore } from "./tools.js";

import type { MessageAttachment } from "../content/types.js";
import type { Orchestrator } from "../core/orchestrator/orchestrator.js";

import type {
  GroupMeta,
  SavedPageRef,
  ShadowClawDatabase,
  StoredMessage,
  Task,
} from "../db/types.js";

import type { StorageStatus } from "../storage/storage.js";

import type {
  ContextUsage,
  ModelDownloadProgressPayload,
  ThinkingLogEntry,
  TokenUsage,
  ToolActivity,
} from "../subsystems/worker/types.js";

import type { A2UIAction } from "../ui/a2ui/types.js";

export type OrchestratorDisplayState =
  | "idle"
  | "thinking"
  | "responding"
  | "error";

type TaskSyncOutboxOperation =
  | {
      type: "upsert";
      id: string;
      task: Task;
      queuedAt: number;
    }
  | {
      type: "delete";
      id: string;
      queuedAt: number;
    };

interface ServerScheduledTask {
  id: string;
  group_id?: string;
  groupId?: string;
  schedule: string;
  prompt: string;
  type?: "prompt" | "tools";
  tools?: string | Task["tools"] | null;
  channel?: string | null;

  enabled: number | boolean;
  last_run?: number | null;
  lastRun?: number | null;
  created_at?: number;
  createdAt?: number;
  name?: string | null;
  order?: number | null;
  task_order?: number | null;
}

function parseTaskTools(value: ServerScheduledTask["tools"]): Task["tools"] {
  if (!value) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function isConfigEnabled(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    return normalized === "true" || normalized === "1";
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return false;
}

/**
 * Lazy-cached probe: returns true only when the server's schedule API is
 * reachable.
 *
 * Returns false immediately when taskServerEnabled is false so no network
 * probes are ever made in static-only deployments.
 */
function isScheduleServerEnabled(enabled: boolean): boolean {
  return !!enabled;
}

/**
 * Sync a task to the server-side SQLite store.
 */
async function syncTaskToServer(
  task: Task,
  baseUrl: string,
  subscriberId: string,
  serverEnabled: boolean,
): Promise<boolean> {
  if (!isScheduleServerEnabled(serverEnabled)) {
    return true; // Silently succeed when server-side tasks are disabled.
  }

  // If the task has no schedule, it belongs only in local storage.
  // We issue a delete to ensure it is removed from the server if it was previously scheduled.
  if (!task.schedule) {
    const deleteResult = await deleteTaskFromServer(
      task.id,
      baseUrl,
      subscriberId,
      serverEnabled,
    );
    return deleteResult !== "failed";
  }

  try {
    const base = baseUrl.replace(/\/$/, "");
    const res = await fetch(`${base}/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...task,
        subscriberId,
      }),
    });

    return res.ok;
  } catch {
    return false;
  }
}

type DeleteTaskServerResult = "deleted" | "missing" | "failed";

/**
 * Delete a task from the server-side SQLite store.
 */
async function deleteTaskFromServer(
  id: string,
  baseUrl: string,
  subscriberId: string,
  serverEnabled: boolean,
): Promise<DeleteTaskServerResult> {
  if (!isScheduleServerEnabled(serverEnabled)) {
    return "missing"; // No server in static-only deployments.
  }

  try {
    const base = baseUrl.replace(/\/$/, "");
    const query = `subscriberId=${encodeURIComponent(subscriberId)}`;
    const res = await fetch(
      `${base}/tasks/${encodeURIComponent(id)}?${query}`,
      {
        method: "DELETE",
      },
    );

    if (res.ok) {
      return "deleted";
    }

    // "Not found" means there is nothing left to delete on the server.
    // Some deployments may also reject DELETE on this endpoint with 405.
    if (res.status === 404 || res.status === 405) {
      return "missing";
    }

    return "failed";
  } catch {
    return "failed";
  }
}

async function fetchServerTasksForGroup(
  groupId: string,
  baseUrl: string,
  subscriberId: string,
  serverEnabled: boolean,
): Promise<Task[] | null> {
  if (!isScheduleServerEnabled(serverEnabled)) {
    return null; // No server in static-only deployments.
  }

  try {
    const base = baseUrl.replace(/\/$/, "");
    const res = await fetch(
      `${base}/tasks?groupId=${encodeURIComponent(groupId)}&subscriberId=${encodeURIComponent(subscriberId)}`,
      {
        method: "GET",
      },
    );

    if (!res.ok || typeof (res as any).json !== "function") {
      return null;
    }

    const payload = await (res as any).json();
    if (!Array.isArray(payload)) {
      return null;
    }

    return payload
      .filter((task): task is ServerScheduledTask => {
        return (
          !!task &&
          typeof task === "object" &&
          typeof (task as any).id === "string" &&
          typeof (task as any).schedule === "string" &&
          typeof (task as any).prompt === "string"
        );
      })
      .map((task) => ({
        id: task.id,
        groupId: task.group_id || task.groupId || groupId,
        schedule: task.schedule,
        type: task.type,
        prompt: task.prompt,
        tools: parseTaskTools(task.tools),
        channel: typeof task.channel === "string" ? task.channel : undefined,

        enabled: task.enabled === true || task.enabled === 1,
        lastRun:
          typeof task.lastRun === "number"
            ? task.lastRun
            : typeof task.last_run === "number"
              ? task.last_run
              : null,
        createdAt:
          typeof task.createdAt === "number"
            ? task.createdAt
            : typeof task.created_at === "number"
              ? task.created_at
              : Date.now(),
        name: typeof task.name === "string" ? task.name : undefined,
        order:
          typeof task.order === "number"
            ? task.order
            : typeof task.task_order === "number"
              ? task.task_order
              : undefined,
      }));
  } catch {
    return null;
  }
}

export interface OrchestratorStoreState {
  isTyping: boolean;
  ready: boolean;
  state: OrchestratorDisplayState;
  messages: StoredMessage[];
  contextUsage: ContextUsage | null;
  files: string[];
  activeGroupId: string;
  pages: SavedPageRef[];
  currentPath: string;
  error: string | null;
  activityLog: ThinkingLogEntry[];
  tokenUsage: TokenUsage | null;
  toolActivity: ToolActivity | null;
  modelDownloadProgress: ModelDownloadProgressPayload | null;
  streamingText: string | null;
}

export function accumulateTokenUsage(
  prev: TokenUsage | null,
  next: TokenUsage,
): TokenUsage {
  const inputTokens = (prev?.inputTokens ?? 0) + (next.inputTokens ?? 0);
  const outputTokens = (prev?.outputTokens ?? 0) + (next.outputTokens ?? 0);
  const cacheReadTokens =
    (prev?.cacheReadTokens ?? 0) + (next.cacheReadTokens ?? 0);
  const cacheCreationTokens =
    (prev?.cacheCreationTokens ?? 0) + (next.cacheCreationTokens ?? 0);

  return {
    groupId: next.groupId || prev?.groupId || "",
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheCreationTokens,
    totalTokens:
      inputTokens + outputTokens + cacheReadTokens + cacheCreationTokens,
    contextLimit: next.contextLimit || prev?.contextLimit || 0,
  };
}

export class OrchestratorStore {
  private static readonly DEFAULT_PAGE_PATH = "MEMORY.md";
  public orchestrator: Orchestrator | null;
  public _activeGroupId: Signal.State<string>;
  public _activePage: Signal.State<string>;
  public _activePinnedPage: Signal.State<SavedPageRef | null>;
  public _defaultPinnedPage: Signal.State<SavedPageRef | null>;
  public _activityLog: Signal.State<ThinkingLogEntry[]>;
  private _activityLogSessionStartedAtByGroup: Map<string, string>;
  private _aguiAdapter: AGUIAdapter | null;
  public _aguiEvent: Signal.State<{ groupId: string; event: any } | null>;
  public _contextUsage: Signal.State<ContextUsage | null>;
  public _currentPath: Signal.State<string>;
  private _db: ShadowClawDatabase | null;
  public _error: Signal.State<string | null>;
  public _files: Signal.State<string[]>;
  public _gitProxyUrl: Signal.State<string>;
  public _groups: Signal.State<GroupMeta[]>;
  private _hadPersistedActivePage: boolean;
  private _initResolve: (() => void) | null;
  public _isTyping: Signal.State<boolean>;
  private _lastOrchestratorDisplayState: OrchestratorDisplayState = "idle";

  public _messages: Signal.State<StoredMessage[]>;
  public _modelDownloadProgress: Signal.State<ModelDownloadProgressPayload | null>;
  private _onlineReplayHandler: (() => void) | null;
  public _pages: Signal.State<SavedPageRef[]>;
  public _peerStateByGroup: Signal.State<Map<string, Record<string, unknown>>>;
  public _proxyUrl: Signal.State<string>;
  public _ready: Signal.State<boolean>;
  public _remoteAgentStatusByGroup: Signal.State<
    Map<string, OrchestratorDisplayState>
  >;
  public _remoteAgentTypingByGroup: Signal.State<Map<string, boolean>>;
  private _replayingTaskSyncOutbox: boolean;
  private _subscriberId: string | null;
  public _sidebarDefaultPage: Signal.State<
    "pages" | "chat" | "tasks" | "files"
  >;
  public _state: Signal.State<OrchestratorDisplayState>;
  public _storageStatus: Signal.State<StorageStatus | null>;
  public _streamingText: Signal.State<string | null>;
  public _tasks: Signal.State<Task[]>;
  private _taskSyncOutbox: TaskSyncOutboxOperation[];
  public _tokenUsage: Signal.State<TokenUsage | null>;
  private _tokenUsageAccumulator: TokenUsage | null = null;
  public _toolActivity: Signal.State<ToolActivity | null>;
  public _unreadGroupIds: Signal.State<Set<string>>;
  public _useProxy: Signal.State<boolean>;
  public _vmBashFullInternetAccess: Signal.State<boolean>;
  private _whenInitialized: Promise<void>;
  private _whenReady: Promise<void>;
  private _readyResolve: (() => void) | null;

  constructor() {
    this._messages = new Signal.State([]);
    this._isTyping = new Signal.State(false);
    this._storageStatus = new Signal.State(null);
    this._toolActivity = new Signal.State(null);
    this._modelDownloadProgress = new Signal.State(null);
    this._activityLog = new Signal.State([]);
    this._state = new Signal.State("idle");
    this._tokenUsage = new Signal.State(null);
    this._error = new Signal.State(null);
    this._activeGroupId = new Signal.State(DEFAULT_GROUP_ID);
    this._ready = new Signal.State(false);
    this._tasks = new Signal.State([]);
    this._files = new Signal.State([]);
    this._currentPath = new Signal.State(".");
    this._groups = new Signal.State([]);
    this._unreadGroupIds = new Signal.State(new Set());
    this._streamingText = new Signal.State(null);
    this._aguiEvent = new Signal.State(null);
    this._contextUsage = new Signal.State(null);
    this._useProxy = new Signal.State(false);
    this._proxyUrl = new Signal.State("/proxy");
    this._gitProxyUrl = new Signal.State("/git-proxy");
    this._vmBashFullInternetAccess = new Signal.State(false);
    this._activePage = new Signal.State("pages");
    this._sidebarDefaultPage = new Signal.State("chat");
    this._pages = new Signal.State([]);
    this._activePinnedPage = new Signal.State(null);
    this._defaultPinnedPage = new Signal.State(null);
    this._remoteAgentStatusByGroup = new Signal.State(new Map());
    this._remoteAgentTypingByGroup = new Signal.State(new Map());
    this._peerStateByGroup = new Signal.State(new Map());
    this._hadPersistedActivePage = false;
    this._initResolve = null;
    this._whenInitialized = new Promise<void>((resolve) => {
      this._initResolve = resolve;
    });
    this._readyResolve = null;
    this._whenReady = new Promise<void>((resolve) => {
      this._readyResolve = resolve;
    });
    this.orchestrator = null;
    this._db = null;
    this._activityLogSessionStartedAtByGroup = new Map();
    this._taskSyncOutbox = [];
    this._replayingTaskSyncOutbox = false;
    this._subscriberId = null;
    this._onlineReplayHandler = null;
    this._aguiAdapter = null;
  }

  get activeGroupId() {
    return this._activeGroupId.get();
  }

  get activePage() {
    return this._activePage.get();
  }

  get activePinnedPage() {
    return this._activePinnedPage.get();
  }

  get defaultPinnedPage(): SavedPageRef | null {
    return this._defaultPinnedPage.get();
  }

  get effectiveDefaultPage(): SavedPageRef | null {
    const pages = this._pages.get();

    return pages[0] || null;
  }

  get activityLog() {
    return this._activityLog.get();
  }

  /**
   * Get the latest AG-UI event for the active conversation, or null.
   */
  get aguiEvent(): { groupId: string; event: any } | null {
    return this._aguiEvent.get();
  }

  /**
   * Clear error
   */
  clearError() {
    this._error.set(null);
    if (this._state.get() === "error") {
      this._state.set("idle");
    }
  }

  /**
   * Get context usage info, or `null` if not yet computed.
   */
  get contextUsage(): ContextUsage | null {
    return this._contextUsage.get();
  }

  get tokenUsageAccumulator(): TokenUsage | null {
    return this._tokenUsageAccumulator;
  }

  get currentPath() {
    return this._currentPath.get();
  }

  get error() {
    return this._error.get();
  }

  get files() {
    return this._files.get();
  }

  getPeerState(groupId: string): Record<string, unknown> | null {
    return this._peerStateByGroup.get().get(groupId) || null;
  }

  /**
   * Get the remote agent status for a specific group.
   * Returns "idle" if no remote status is set.
   */
  getRemoteAgentStatus(groupId: string): OrchestratorDisplayState {
    const statusMap = this._remoteAgentStatusByGroup.get();

    return statusMap.get(groupId) || "idle";
  }

  /**
   * Get current state
   */
  getState(): OrchestratorStoreState {
    return {
      messages: this.messages,
      isTyping: this.isTyping,
      toolActivity: this.toolActivity,
      activityLog: this.activityLog,
      state: this.state,
      tokenUsage: this.tokenUsage,
      modelDownloadProgress: this.modelDownloadProgress,
      error: this.error,
      activeGroupId: this.activeGroupId,
      pages: this.pages,
      ready: this.ready,
      files: this.files,
      currentPath: this.currentPath,
      streamingText: this.streamingText,
      contextUsage: this.contextUsage,
    };
  }

  /**
   * Get all tasks for backup
   */
  getTasksForBackup(): Task[] {
    return this._tasks.get();
  }

  get gitProxyUrl() {
    return this._gitProxyUrl.get();
  }

  get groups() {
    return this._groups.get();
  }

  get hadPersistedActivePage() {
    return this._hadPersistedActivePage;
  }

  /**
   * Get the remote agent typing status for a specific group.
   */
  isRemoteAgentTyping(groupId: string): boolean {
    const typingMap = this._remoteAgentTypingByGroup.get();

    return typingMap.get(groupId) || false;
  }

  get isTyping() {
    return this._isTyping.get();
  }

  get messages() {
    return this._messages.get();
  }

  get modelDownloadProgress() {
    return this._modelDownloadProgress.get();
  }

  get pages() {
    return this._pages.get();
  }

  get proxyUrl() {
    return this._proxyUrl.get();
  }

  get ready() {
    return this._ready.get();
  }

  /**
   * Send a message
   */
  sendMessage(
    text: string,
    attachments: MessageAttachment[] = [],
    a2uiAction?: A2UIAction,
  ): void {
    if (a2uiAction !== undefined) {
      this.orchestrator?.browserChat?.submit(
        text,
        this._activeGroupId.get(),
        attachments,
        a2uiAction,
      );
    } else {
      this.orchestrator?.browserChat?.submit(
        text,
        this._activeGroupId.get(),
        attachments,
      );
    }
  }

  /**
   * Set active group
   */
  setActiveGroup(
    db: ShadowClawDatabase,
    groupId: string,
    clearUnread = this._activePage.get() === "chat",
  ) {
    this._activeGroupId.set(groupId);
    this._messages.set([]);
    this._activityLog.set([]);
    this._error.set(null);
    this._isTyping.set(false);
    this._toolActivity.set(null);
    this._modelDownloadProgress.set(null);
    this._streamingText.set(null);
    this._currentPath.set(".");

    // Clear unread indicator for the group being viewed
    if (clearUnread) {
      const unread = new Set(this._unreadGroupIds.get());
      if (unread.delete(groupId)) {
        this._unreadGroupIds.set(unread);
      }
    }

    this.loadHistory();
    this.loadTasks(db);
    this.loadFiles(db);
  }

  setReady(ready: boolean = true): void {
    this._ready.set(ready);
    if (ready && this._readyResolve) {
      this._readyResolve();
    }
  }

  /**
   * Set the remote agent status for a specific group.
   */
  setRemoteAgentStatus(
    groupId: string,
    status: OrchestratorDisplayState,
  ): void {
    const statusMap = this._remoteAgentStatusByGroup.get();
    const newMap = new Map(statusMap);
    newMap.set(groupId, status);
    this._remoteAgentStatusByGroup.set(newMap);
  }

  /**
   * Set the remote agent typing status for a specific group.
   */
  setRemoteAgentTyping(groupId: string, typing: boolean): void {
    const typingMap = this._remoteAgentTypingByGroup.get();
    const newMap = new Map(typingMap);
    if (typing) {
      newMap.set(groupId, true);
    } else {
      newMap.delete(groupId);
    }

    this._remoteAgentTypingByGroup.set(newMap);
  }

  get sidebarDefaultPage() {
    return this._sidebarDefaultPage.get();
  }

  get state() {
    return this._state.get();
  }

  /**
   * Stop the active in-flight request for the current group.
   */
  stopCurrentRequest() {
    this.orchestrator?.stopCurrentRequest?.(this._activeGroupId.get());
  }

  get storageStatus() {
    return this._storageStatus.get();
  }

  /**
   * Get the accumulated streaming text, or `null` if no stream is active.
   */
  get streamingText(): string | null {
    return this._streamingText.get();
  }

  /**
   * Request a manual host -> VM workspace sync for the active group.
   */
  syncHostWorkspaceToVM() {
    syncTerminalWorkspace(this.orchestrator!, this._activeGroupId.get());
  }

  /**
   * Request a manual VM -> host workspace flush for the active group.
   */
  syncVMWorkspaceToHost() {
    flushTerminalWorkspace(this.orchestrator!, this._activeGroupId.get());
  }

  get tasks() {
    return this._tasks.get();
  }

  get tokenUsage() {
    return this._tokenUsage.get();
  }

  get toolActivity() {
    return this._toolActivity.get();
  }

  /**
   * Get the set of groupIds with unread messages.
   */
  get unreadGroupIds(): Set<string> {
    return this._unreadGroupIds.get();
  }

  get useProxy() {
    return this._useProxy.get();
  }

  get vmBashFullInternetAccess() {
    return this._vmBashFullInternetAccess.get();
  }

  get whenInitialized(): Promise<void> {
    return this._whenInitialized;
  }

  /** Allow embedded declarative site defaults to apply again on next boot. */
  async resetSiteConfigSeed(db: ShadowClawDatabase): Promise<void> {
    await setConfig(db, CONFIG_KEYS.SITE_CONFIG_SEEDED, false);
  }

  get whenReady(): Promise<void> {
    return this._whenReady;
  }

  async addPage(
    db: ShadowClawDatabase,
    path: string,
    groupId: string = this._activeGroupId.get(),
  ): Promise<void> {
    const normalized =
      groupId === DEFAULT_GROUP_ID ? this.normalizePagePath(path) : path.trim();
    if (!normalized) {
      return;
    }

    const pages = this._pages.get();
    if (
      pages.some(
        (entry) => entry.path === normalized && entry.groupId === groupId,
      )
    ) {
      return;
    }

    this.setPages([
      ...pages,
      {
        groupId,
        path: normalized,
      },
    ]);

    await unsuppressPage(db, groupId, normalized);

    if (
      groupId === DEFAULT_GROUP_ID &&
      normalized === DEFAULT_MAIN_GROUP_MEMORY_PATH
    ) {
      await setMainGroupMemorySuppressed(db, false);
    }

    await this.persistPages(db);
  }

  /**
   * Clear all tasks for the current group
   */
  async clearAllTasks(db: ShadowClawDatabase): Promise<void> {
    const allTasks = await getAllTasks(db);
    const currentGroupId = this._activeGroupId.get();
    const groupTasks = allTasks.filter((t) => t.groupId === currentGroupId);

    const subscriberId = await this.getSubscriberId(db);

    for (const task of groupTasks) {
      const serverResult = await deleteTaskFromServer(
        task.id,
        this.getTaskServerBaseUrl(),
        subscriberId,
        this.orchestrator?.taskServerEnabled ?? false,
      );
      if (serverResult === "failed") {
        console.warn(
          `Failed to delete task "${task.id}" from server — queued for replay.`,
        );
        await this.queueTaskSyncOutboxOperation(db, {
          type: "delete",
          id: task.id,
          queuedAt: Date.now(),
        });

        continue;
      }

      await deleteTask(db, task.id);
    }

    await this.loadTasks(db);
  }

  /**
   * Clone a conversation (metadata + messages + tasks + MEMORY.md)
   * and switch to the clone.
   */
  async cloneConversation(
    db: ShadowClawDatabase,
    sourceGroupId: string,
  ): Promise<GroupMeta | null> {
    const clone = await cloneGroup(db, sourceGroupId);
    if (!clone) {
      return null;
    }

    await cloneGroupMessages(db, sourceGroupId, clone.groupId);
    await cloneGroupTasks(db, sourceGroupId, clone.groupId);

    try {
      const memory = await readGroupFile(db, sourceGroupId, "MEMORY.md");
      if (memory) {
        await writeGroupFile(db, clone.groupId, "MEMORY.md", memory);
      }
    } catch {
      // Source has no MEMORY.md — nothing to copy
    }

    try {
      await copyGroupDirectory(db, sourceGroupId, clone.groupId, "attachments");
    } catch {
      // Source has no attachments directory — nothing to copy
    }

    await this.loadGroups(db);
    this.setActiveGroup(db, clone.groupId);

    return clone;
  }

  /**
   * Compact context
   */
  async compactContext(db: ShadowClawDatabase): Promise<any> {
    if (!this.orchestrator) return;
    return compactContext(this.orchestrator, db, this._activeGroupId.get());
  }

  /**
   * Create a conversation
   */
  async createConversation(
    db: ShadowClawDatabase,
    name: string,
  ): Promise<GroupMeta> {
    const group = await createGroup(db, name);
    await this.loadGroups(db);
    this.setActiveGroup(db, group.groupId);

    return group;
  }

  /**
   * Delete a conversation. Refuses to delete the last remaining group.
   * If the deleted group is active, switches to the first remaining group.
   */
  async deleteConversation(
    db: ShadowClawDatabase,
    groupId: string,
  ): Promise<void> {
    const groups = this._groups.get();
    if (groups.length <= 1) {
      return;
    }

    await deleteGroupMetadata(db, groupId);
    await clearGroupMessages(db, groupId);
    await this.loadGroups(db);

    if (this._activeGroupId.get() === groupId) {
      const remaining = this._groups.get();
      const next = remaining[0]?.groupId || DEFAULT_GROUP_ID;
      this.setActiveGroup(db, next);
    }
  }

  /**
   * Delete a message
   */
  async deleteMessage(db: ShadowClawDatabase, id: string): Promise<void> {
    await deleteMessage(db, id);
    await this.loadHistory();
    if (this.orchestrator?.refreshContextUsage) {
      await this.orchestrator.refreshContextUsage(
        db,
        this._activeGroupId.get(),
      );
    }
  }

  /**
   * Delete a task
   */
  async deleteTask(db: ShadowClawDatabase, id: string): Promise<void> {
    const subscriberId = await this.getSubscriberId(db);
    const serverResult = await deleteTaskFromServer(
      id,
      this.getTaskServerBaseUrl(),
      subscriberId,
      this.orchestrator?.taskServerEnabled ?? false,
    );
    if (serverResult === "failed") {
      console.warn("Failed to delete task from server — queued for replay.");
      await this.queueTaskSyncOutboxOperation(db, {
        type: "delete",
        id,
        queuedAt: Date.now(),
      });

      throw new Error(
        "Failed to delete scheduled task on server; task kept locally.",
      );
    }

    await deleteTask(db, id);
    await this.loadTasks(db);
  }

  /**
   * Ensure a PeerJS conversation exists. If not, create it.
   */
  async ensurePeerConversation(
    db: ShadowClawDatabase,
    remotePeerId: string,
  ): Promise<string> {
    const groupId = `peer:${remotePeerId}`;
    const groups = await listGroups(db);
    const existing = groups.find((g) => g.groupId === groupId);
    if (!existing) {
      const metadata = [...groups];
      let aliasName = "";
      if (this.orchestrator?.peerjsPeerAliases) {
        // Find if any alias matches this remotePeerId
        for (const [alias, id] of Object.entries(
          this.orchestrator.peerjsPeerAliases,
        )) {
          if (id === remotePeerId) {
            aliasName = alias;

            break;
          }
        }
      }

      metadata.push({
        groupId,
        name: `Peer: ${aliasName ? aliasName : remotePeerId.substring(0, 8)}`,
        createdAt: Date.now(),
      });
      await saveGroupMetadata(db, metadata);
      await this.loadGroups(db);
    }

    return groupId;
  }

  /**
   * Request storage access
   */
  async grantStorageAccess(db: ShadowClawDatabase): Promise<void> {
    try {
      await requestStorageAccess(db);
      await this.loadFiles(db); // Refresh files and status after granting access
    } catch (err) {
      console.error("Failed to grant storage access:", err);
    }
  }

  /**
   * Initialize the store with an Orchestrator instance
   */
  async init(db: ShadowClawDatabase, orch: Orchestrator): Promise<void> {
    this._db = db;
    this.orchestrator = orch;

    this._taskSyncOutbox = this.parseTaskSyncOutbox(
      await getConfig(db, CONFIG_KEYS.TASK_SYNC_OUTBOX),
    );

    await this.replayTaskSyncOutbox(db);

    if (typeof window !== "undefined" && !this._onlineReplayHandler) {
      this._onlineReplayHandler = () => {
        if (this._db) {
          void this.replayTaskSyncOutbox(this._db);
        }
      };

      window.addEventListener("online", this._onlineReplayHandler);
    }

    // Subscribe to orchestrator events
    orch.events.on("a2ui-surface", async ({ groupId, envelope }) => {
      if (groupId) {
        void this.ensureGroupExists(db, groupId);
      }

      const messages = this._messages.get();
      const existingMsgIndex = messages.findIndex((m) =>
        m.a2uiEnvelopes?.some((e) => e.surfaceId === envelope.surfaceId),
      );

      if (existingMsgIndex !== -1) {
        const existingMsg = messages[existingMsgIndex];
        const updatedEnvelopes = [
          ...(existingMsg.a2uiEnvelopes || []),
          envelope,
        ];
        const updatedMsg = {
          ...existingMsg,
          a2uiEnvelopes: updatedEnvelopes,
        };
        const updatedMessages = [...messages];
        updatedMessages[existingMsgIndex] = updatedMsg;
        this._messages.set(updatedMessages);

        await saveMessage(db, updatedMsg);
      } else {
        const isFromMe = !groupId.startsWith("peer:");
        const newMsg: StoredMessage = {
          id: ulid(),
          groupId,
          sender: isFromMe ? "agent" : groupId.replace(/^peer:/, ""),
          content: "",
          timestamp: Date.now(),
          channel: groupId.startsWith("peer:") ? "peerjs" : "browser",
          isFromMe,
          isTrigger: false,
          a2uiEnvelopes: [envelope],
        };

        if (groupId === this._activeGroupId.get()) {
          this._messages.set([...messages, newMsg]);
        }

        await saveMessage(db, newMsg);
      }
    });

    orch.events.on("message", (msg) => {
      if (msg.groupId) {
        void this.ensureGroupExists(db, msg.groupId, msg.timestamp);
      }

      // Only append messages belonging to the active conversation
      if (msg.groupId && msg.groupId !== this._activeGroupId.get()) {
        // Track as unread for the sidebar pulse indicator
        const unread = new Set(this._unreadGroupIds.get());
        unread.add(msg.groupId);
        this._unreadGroupIds.set(unread);

        return;
      }

      // When a streaming response finalizes, the `response` message arrives
      // but the streaming bubble already showed the text. Don't double-append;
      // just clear streaming state and let the persisted message appear.
      if (msg.isFromMe && this._streamingText.get() !== null) {
        this._streamingText.set(null);
      }

      this._messages.set([...this._messages.get(), msg]);
    });

    orch.events.on("typing", ({ groupId, typing }) => {
      if (groupId && groupId !== this._activeGroupId.get()) {
        return;
      }

      this._isTyping.set(typing);
    });

    orch.events.on("tool-activity", ({ groupId, tool, status }) => {
      if (groupId && groupId !== this._activeGroupId.get()) {
        return;
      }

      this._toolActivity.set(status === "running" ? { tool, status } : null);
    });

    orch.events.on("model-download-progress", (payload) => {
      this._modelDownloadProgress.set(
        payload.status === "done" ? null : payload,
      );
    });

    orch.events.on("thinking-log", (entry) => {
      // Only show activity for the active conversation
      if (entry.groupId && entry.groupId !== this._activeGroupId.get()) {
        return;
      }

      // Reset log when a new invocation starts
      if (entry.level === "info" && entry.label === "Starting") {
        this._activityLog.set([entry]);
      } else {
        this._activityLog.set([...this._activityLog.get(), entry]);
      }

      void this.forwardActivityLogEntryToServer(entry);
    });

    orch.events.on("state-change", (payload) => {
      const state = typeof payload === "string" ? payload : payload.state;
      const groupId = typeof payload === "string" ? undefined : payload.groupId;

      if (groupId && groupId !== this._activeGroupId.get()) {
        return;
      }

      const prev = this._lastOrchestratorDisplayState;
      this._lastOrchestratorDisplayState = state;
      this._state.set(state);

      if (state === "idle") {
        this._toolActivity.set(null);
        this._modelDownloadProgress.set(null);
        this._streamingText.set(null);
      }

      if (
        state === "thinking" &&
        prev !== "thinking" &&
        prev !== "responding"
      ) {
        this._tokenUsageAccumulator = null;
      }
    });

    orch.events.on("error", ({ error }) => {
      console.error("[Orchestrator Error Event]", error);

      this._error.set(error);
      this._state.set("error");
    });

    orch.events.on("session-reset", () => {
      this._messages.set([]);
      this._activityLog.set([]);
      this._tokenUsageAccumulator = null;
      this._tokenUsage.set(null);
      this._contextUsage.set(null);
      this._toolActivity.set(null);
      this._modelDownloadProgress.set(null);
      this._streamingText.set(null);
      this._isTyping.set(false);
      this._state.set("idle");
    });

    orch.events.on("context-compacted", (payload) => {
      // Only reload history if the compacted group is the one being viewed
      if (payload?.groupId && payload.groupId !== this._activeGroupId.get()) {
        return;
      }

      this._tokenUsage.set(null);
      this.loadHistory();
    });

    orch.events.on("token-usage", (usage: TokenUsage | null) => {
      if (!usage) {
        return;
      }

      if (usage.groupId && usage.groupId !== this._activeGroupId.get()) {
        return;
      }

      const accumulated = accumulateTokenUsage(
        this._tokenUsageAccumulator,
        usage,
      );

      this._tokenUsageAccumulator = accumulated;
      this._tokenUsage.set(accumulated);

      // Refresh context usage so the UI reflects the actual token usage from the API
      if (this.orchestrator?.refreshContextUsage && this._db) {
        void this.orchestrator.refreshContextUsage(
          this._db,
          this._activeGroupId.get(),
        );
      }
    });

    orch.events.on("context-usage", (usage) => {
      this._contextUsage.set(usage);
    });

    orch.events.on("ready", () => {
      this._ready.set(true);
    });

    orch.events.on("task-change", () => {
      this.loadTasks(db);
    });

    orch.events.on("file-change", () => {
      this.loadFiles(db);
    });

    // --- Streaming events ---

    orch.events.on("streaming-start", ({ groupId }) => {
      if (groupId !== this._activeGroupId.get()) {
        return;
      }

      this._streamingText.set("");
    });

    orch.events.on("streaming-chunk", ({ groupId, text }) => {
      if (groupId !== this._activeGroupId.get()) {
        return;
      }

      const current = this._streamingText.get();
      this._streamingText.set((current || "") + text);
    });

    orch.events.on("streaming-end", ({ groupId }) => {
      if (groupId !== this._activeGroupId.get()) {
        return;
      }

      // Tool calls are about to run; clear the streaming bubble
      this._streamingText.set(null);
    });

    orch.events.on("streaming-done", ({ groupId }) => {
      if (groupId !== this._activeGroupId.get()) {
        return;
      }
    });

    orch.events.on("streaming-error", ({ groupId }) => {
      if (groupId !== this._activeGroupId.get()) {
        return;
      }
    });

    // --- AG-UI adapter (emits AG-UI CustomEvents for all conversations) ---
    this._aguiAdapter = new AGUIAdapter(orch.events);
    this._aguiAdapter.start();

    // Forward AG-UI DOM events into a reactive signal for the active group
    if (typeof window !== "undefined") {
      window.addEventListener("shadow-claw-agui-event", ((
        e: CustomEvent<{ groupId: string; event: unknown }>,
      ) => {
        if (e.detail.groupId === this._activeGroupId.get()) {
          this._aguiEvent.set(e.detail as any);
        }

        const ev = e.detail.event as any;
        if (ev) {
          if (ev.type === "STATE_SNAPSHOT") {
            const currentMap = new Map(this._peerStateByGroup.get());
            currentMap.set(e.detail.groupId, ev.snapshot || {});
            this._peerStateByGroup.set(currentMap);
          } else if (ev.type === "STATE_DELTA") {
            const currentMap = new Map(this._peerStateByGroup.get());
            const existingState = currentMap.get(e.detail.groupId) || {};
            const patched = applyJsonPatch(existingState, ev.delta || []);
            currentMap.set(e.detail.groupId, patched);
            this._peerStateByGroup.set(currentMap);
          }
        }
      }) as EventListener);
    }

    // Initialize custom element security from DB and install registry guard
    await loadCustomElementSecurityFromDb(db);
    installCustomElementsRegistryGuard();
    installCustomElementDomGuard();

    // Seed static main site files (including declarative tools) first
    const rawPagesList = await getConfig(db, CONFIG_KEYS.PAGES_LIST);
    const existingPages =
      rawPagesList !== null && rawPagesList !== undefined
        ? this.parsePagesList(rawPagesList)
        : [];
    const seeded = await seedStaticMainSite(
      db,
      DEFAULT_GROUP_ID,
      existingPages,
    );
    if (seeded.length > 0) {
      this.setPages(seeded);
      await this.persistPages(db);
    } else {
      await this.ensureDefaultPage(db);
    }

    // Apply declarative site config defaults (from embedded site-config.json) on first boot
    await this.applySiteConfigDefaults(db);

    // Restore last-active conversation, then load data
    await this.loadGroups(db);
    const lastGroup = await getConfig(db, CONFIG_KEYS.LAST_ACTIVE_GROUP);
    if (lastGroup && lastGroup !== this._activeGroupId.get()) {
      this._activeGroupId.set(lastGroup);
    }

    const lastPage = (await getConfig(db, CONFIG_KEYS.LAST_ACTIVE_PAGE)) as
      | string
      | null;
    if (lastPage) {
      this._activePage.set(lastPage);
      this._hadPersistedActivePage = true;
    }

    const sidebarDefaultPageRaw = await getConfig(
      db,
      CONFIG_KEYS.SIDEBAR_DEFAULT_PAGE,
    );
    this._sidebarDefaultPage.set(
      this.normalizeSidebarDefaultPage(sidebarDefaultPageRaw),
    );

    const lastPinnedPageRaw = await getConfig(
      db,
      CONFIG_KEYS.LAST_SELECTED_PINNED_PAGE,
    );
    if (lastPinnedPageRaw) {
      try {
        const parsed = JSON.parse(lastPinnedPageRaw as string);
        if (
          parsed &&
          typeof parsed.path === "string" &&
          typeof parsed.groupId === "string"
        ) {
          this._activePinnedPage.set(parsed);
        }
      } catch {}
    }

    // Initialize proxy values from orchestrator
    if (this.orchestrator) {
      this._useProxy.set(this.orchestrator.useProxy);
      this._proxyUrl.set(this.orchestrator.proxyUrl);
      this._gitProxyUrl.set(this.orchestrator.gitProxyUrl);
      this._vmBashFullInternetAccess.set(
        this.orchestrator.vmBashFullInternetAccess,
      );
    }

    await Promise.all([
      this.loadHistory(),
      this.loadTasks(db),
      this.loadFiles(db),
    ]);

    this._initResolve?.();

    this.scheduleBackgroundStaticMainSiteSeeding(db);
  }

  /**
   * Load files
   */
  async loadFiles(db: ShadowClawDatabase): Promise<void> {
    const groupId = this._activeGroupId.get();
    const currentPath = this._currentPath.get();
    try {
      this._storageStatus.set(await getStorageStatus(db));

      const files = await listGroupFiles(db, groupId, currentPath);

      this._files.set(files);
    } catch (err) {
      console.error("Failed to load files in store:", err);
    }
  }

  /**
   * Load conversations
   */
  async loadGroups(db: ShadowClawDatabase): Promise<void> {
    const groups = await listGroups(db);
    this._groups.set(groups);
  }

  /**
   * Load message history
   */
  async loadHistory() {
    const groupId = this._activeGroupId.get();
    const msgs = await getRecentMessages(groupId, 200);

    // Guard: if the user switched conversations during the async DB query,
    // discard these results to avoid overwriting the new conversation's messages.
    if (this._activeGroupId.get() !== groupId) {
      return;
    }

    this._messages.set(msgs);
  }

  /**
   * Load tasks
   */
  async loadTasks(db: ShadowClawDatabase): Promise<void> {
    const currentGroupId = this._activeGroupId.get();
    const allLocalTasks = await getAllTasks(db);
    const localGroupTasks = allLocalTasks.filter(
      (t) => t.groupId === currentGroupId,
    );
    localGroupTasks.sort((a, b) => {
      const oA = a.order ?? 0;
      const oB = b.order ?? 0;
      if (oA !== oB) {
        return oA - oB;
      }
      return a.createdAt - b.createdAt;
    });
    this._tasks.set(localGroupTasks);

    const subscriberId = await this.getSubscriberId(db);

    // Reconcile server-side scheduled tasks into local IndexedDB so
    // server tasks become visible in the UI and can be deleted.
    const serverGroupTasks = await fetchServerTasksForGroup(
      currentGroupId,
      this.getTaskServerBaseUrl(),
      subscriberId,
      this.orchestrator?.taskServerEnabled ?? false,
    );
    if (!serverGroupTasks) {
      return;
    }

    const localTaskIds = new Set(allLocalTasks.map((task) => task.id));
    const serverOnlyTasks = serverGroupTasks.filter(
      (task) => !localTaskIds.has(task.id),
    );

    if (serverOnlyTasks.length === 0) {
      return;
    }

    for (const task of serverOnlyTasks) {
      await saveTask(db, task);
    }

    const combined = [...localGroupTasks, ...serverOnlyTasks];
    combined.sort((a, b) => {
      const oA = a.order ?? 0;
      const oB = b.order ?? 0;
      if (oA !== oB) {
        return oA - oB;
      }
      return a.createdAt - b.createdAt;
    });
    this._tasks.set(combined);
  }

  /**
   * Navigate back to parent folder
   */
  async navigateBackFolder(db: ShadowClawDatabase): Promise<void> {
    const currentPath = this._currentPath.get();
    if (currentPath === ".") {
      return;
    }

    const parts = currentPath.split("/").filter(Boolean);

    parts.pop();

    const newPath = parts.length === 0 ? "." : parts.join("/");

    await this.setCurrentPath(db, newPath);
  }

  /**
   * Navigate into a folder
   */
  async navigateIntoFolder(
    db: ShadowClawDatabase,
    folderName: string,
  ): Promise<void> {
    const currentPath = this._currentPath.get();
    const newPath =
      currentPath === "."
        ? folderName.replace(/\/$/, "")
        : `${currentPath}/${folderName.replace(/\/$/, "")}`;

    try {
      await this.setCurrentPath(db, newPath);
    } catch (err) {
      console.error("Failed to navigate into folder in store:", err);
      showError(`Folder not found: ${newPath}`, 4500);
      await this.loadFiles(db);
    }
  }

  /**
   * Start a new session
   */
  async newSession(db: ShadowClawDatabase): Promise<void> {
    await this.orchestrator?.newSession?.(db, this._activeGroupId.get());
    await this.loadHistory();
  }

  async removePage(
    db: ShadowClawDatabase,
    path: string,
    groupId: string = this._activeGroupId.get(),
  ): Promise<void> {
    const normalized = this.normalizePagePath(path);
    if (!normalized) {
      return;
    }

    const pages = this._pages.get();
    if (
      !pages.some(
        (entry) => entry.path === normalized && entry.groupId === groupId,
      )
    ) {
      return;
    }

    const remainingPages = pages.filter(
      (entry) => !(entry.path === normalized && entry.groupId === groupId),
    );

    await suppressPage(db, groupId, normalized);

    const removingMainMemoryPage =
      groupId === DEFAULT_GROUP_ID &&
      normalized === DEFAULT_MAIN_GROUP_MEMORY_PATH;

    if (removingMainMemoryPage) {
      await setMainGroupMemorySuppressed(db, true);
    }

    const mainMemorySuppressed =
      removingMainMemoryPage || (await isMainGroupMemorySuppressed(db));

    if (remainingPages.length === 0 && !mainMemorySuppressed) {
      const hasWorkspaceReadme = await ensureMainGroupMemory(
        db,
        DEFAULT_GROUP_ID,
      );

      if (hasWorkspaceReadme) {
        remainingPages.push({
          groupId: DEFAULT_GROUP_ID,
          path: OrchestratorStore.DEFAULT_PAGE_PATH,
        });
      } else {
        return;
      }
    }

    this.setPages(remainingPages);
    const def = this._defaultPinnedPage.get();
    if (def && def.path === normalized && def.groupId === groupId) {
      await this.setDefaultPinnedPage(db, null);
    }
    await this.persistPages(db);
  }

  async removeAllPages(db: ShadowClawDatabase): Promise<void> {
    const pages = [...this._pages.get()];
    for (const page of pages) {
      await suppressPage(db, page.groupId, page.path);
      if (
        page.groupId === DEFAULT_GROUP_ID &&
        page.path === DEFAULT_MAIN_GROUP_MEMORY_PATH
      ) {
        await setMainGroupMemorySuppressed(db, true);
      }
    }

    this.setPages([]);
    await this.setDefaultPinnedPage(db, null);
    await this.setActivePinnedPage(db, null);
    await this.persistPages(db);
  }

  /**
   * Rename a conversation
   */
  async renameConversation(
    db: ShadowClawDatabase,
    groupId: string,
    newName: string,
  ): Promise<void> {
    await renameGroup(db, groupId, newName);
    await this.loadGroups(db);
  }

  /**
   * Reorder conversations
   */
  async reorderConversations(
    db: ShadowClawDatabase,
    groupIds: string[],
  ): Promise<void> {
    await reorderGroups(db, groupIds);
    await this.loadGroups(db);
  }

  /**
   * Reorder tasks in a group
   */
  async reorderTasks(
    db: ShadowClawDatabase,
    groupId: string,
    taskIds: string[],
  ): Promise<void> {
    const updatedTasks = await reorderTasks(db, groupId, taskIds);
    await this.loadTasks(db);

    // Sync all updated tasks to the server
    const subscriberId = await this.getSubscriberId(db);
    for (const task of updatedTasks) {
      const base = this.getTaskServerBaseUrl();
      const ok = await syncTaskToServer(
        task,
        base,
        subscriberId,
        this.orchestrator?.taskServerEnabled ?? false,
      );
      if (!ok) {
        console.warn(
          `Failed to sync reordered task "${task.id}" to server — queued for replay.`,
        );
        await this.queueTaskSyncOutboxOperation(db, {
          type: "upsert",
          id: task.id,
          task,
          queuedAt: Date.now(),
        });
      }
    }
  }

  async replayTaskSyncOutbox(db: ShadowClawDatabase): Promise<void> {
    if (this._replayingTaskSyncOutbox || this._taskSyncOutbox.length === 0) {
      return;
    }

    this._replayingTaskSyncOutbox = true;

    try {
      const subscriberId = await this.getSubscriberId(db);
      const remaining: TaskSyncOutboxOperation[] = [];

      for (const op of this._taskSyncOutbox) {
        const base = this.getTaskServerBaseUrl();
        const ok =
          op.type === "upsert"
            ? await syncTaskToServer(
                op.task,
                base,
                subscriberId,
                this.orchestrator?.taskServerEnabled ?? false,
              )
            : await deleteTaskFromServer(
                op.id,
                base,
                subscriberId,
                this.orchestrator?.taskServerEnabled ?? false,
              );

        if (!ok) {
          remaining.push(op);
        }
      }

      this._taskSyncOutbox = this.compactTaskSyncOutbox(remaining);
      await this.persistTaskSyncOutbox(db);
    } finally {
      this._replayingTaskSyncOutbox = false;
    }
  }

  /**
   * Reset to root folder
   */
  async resetToRootFolder(db: ShadowClawDatabase): Promise<void> {
    await this.setCurrentPath(db, ".");
  }

  async restartCurrentRequest(): Promise<boolean> {
    if (!this.orchestrator?.restartCurrentRequest) {
      return false;
    }

    return this.orchestrator.restartCurrentRequest(this._activeGroupId.get());
  }

  /**
   * Restore tasks from backup
   */
  async restoreTasksFromBackup(
    db: ShadowClawDatabase,
    tasks: Task[],
  ): Promise<void> {
    // First, clear all existing tasks
    await this.clearAllTasks(db);

    const currentGroupId = this._activeGroupId.get();
    // Save each task with current group ID and new IDs
    for (const task of tasks) {
      const taskToSave = {
        ...task,
        groupId: currentGroupId,
        id: crypto.randomUUID
          ? crypto.randomUUID()
          : `task-${Date.now()}-${Math.random()}`,
      };

      await this.upsertTask(db, taskToSave, { reloadTasks: false });
    }

    await this.loadTasks(db);
  }

  /**
   * Run a task
   */
  async runTask(task: Task, isManual: boolean = false): Promise<void> {
    const db = this._db;
    if (isManual && db) {
      await updateTaskLastRun(task.id, Date.now());
      await this.loadTasks(db);
    }

    if (
      task.type === "tools" &&
      Array.isArray(task.tools) &&
      task.tools.length > 0
    ) {
      if (this.orchestrator?.agentWorker) {
        const executionGroupId = task.subagent
          ? `subagent:${ulid()}`
          : task.groupId;
        this.orchestrator.agentWorker.postMessage({
          type: "execute-task-tools",
          payload: {
            groupId: executionGroupId,
            tools: task.tools,
            isManual,
          },
        });
      } else {
        console.error("Agent worker not available to execute tool task.");
      }
    } else {
      // Send the prompt directly to the conversation that owns the task,
      // NOT the currently-visible conversation — scheduled tasks must always
      // fire in the conversation they were created in.
      if (!task.groupId) {
        console.error(
          `Task ${task.id} has no groupId - refusing to execute in active conversation to prevent context pollution.`,
        );

        return;
      }

      this.orchestrator?.browserChat?.submit(
        task.prompt,
        task.groupId,
        [],
        undefined,
        task.freshContext,
        task.subagent,
      );
    }
  }

  /**
   * Set active page
   */
  async setActivePage(db: ShadowClawDatabase, page: string) {
    this._activePage.set(page);
    await setConfig(db, CONFIG_KEYS.LAST_ACTIVE_PAGE, page);

    const nextSidebarDefaultPage =
      this.resolveSidebarDefaultPageForActivePage(page);
    if (nextSidebarDefaultPage !== this._sidebarDefaultPage.get()) {
      this._sidebarDefaultPage.set(nextSidebarDefaultPage);
      await setConfig(
        db,
        CONFIG_KEYS.SIDEBAR_DEFAULT_PAGE,
        nextSidebarDefaultPage,
      );
    }
  }

  async setActivePinnedPage(
    db: ShadowClawDatabase,
    page: SavedPageRef | null,
  ): Promise<void> {
    this._activePinnedPage.set(page);
    if (page) {
      await setConfig(
        db,
        CONFIG_KEYS.LAST_SELECTED_PINNED_PAGE,
        JSON.stringify(page),
      );
    } else {
      await setConfig(db, CONFIG_KEYS.LAST_SELECTED_PINNED_PAGE, null);
    }
  }

  async setDefaultPinnedPage(
    db: ShadowClawDatabase,
    page: SavedPageRef | null,
  ): Promise<void> {
    const current = this._defaultPinnedPage.get();
    const isSame =
      current &&
      page &&
      current.path === page.path &&
      current.groupId === page.groupId;
    const nextValue = isSame ? null : page;

    this._defaultPinnedPage.set(nextValue);
    if (nextValue) {
      await setConfig(
        db,
        CONFIG_KEYS.DEFAULT_PINNED_PAGE,
        JSON.stringify(nextValue),
      );
    } else {
      await setConfig(db, CONFIG_KEYS.DEFAULT_PINNED_PAGE, null);
    }
  }

  async reorderPages(
    db: ShadowClawDatabase,
    pages: SavedPageRef[],
  ): Promise<void> {
    this.setPages(pages);
    await this.persistPages(db);
  }

  /**
   * Set and load the current file browser path.
   */
  async setCurrentPath(db: ShadowClawDatabase, path: string): Promise<void> {
    const normalizedPath = path.replace(/^\/+|\/+$/g, "");
    const nextPath = normalizedPath ? normalizedPath : ".";
    const groupId = this._activeGroupId.get();

    this._storageStatus.set(await getStorageStatus(db));

    const files = await listGroupFiles(db, groupId, nextPath);

    this._currentPath.set(nextPath);
    this._files.set(files);
  }

  /**
   * Set Git CORS proxy URL
   */
  async setGitProxyUrl(db: ShadowClawDatabase, url: string): Promise<void> {
    if (this.orchestrator) {
      await setGitProxyUrl(this.orchestrator, db, url);
      this._gitProxyUrl.set(this.orchestrator.gitProxyUrl);
    }
  }

  /**
   * Set global CORS proxy URL
   */
  async setProxyUrl(db: ShadowClawDatabase, url: string): Promise<void> {
    if (this.orchestrator) {
      await setProxyUrl(this.orchestrator, db, url);

      this._proxyUrl.set(this.orchestrator.proxyUrl);
    }
  }

  /**
   * Toggle global CORS proxy
   */
  async setUseProxy(db: ShadowClawDatabase, enabled: boolean): Promise<void> {
    if (this.orchestrator) {
      await setUseProxy(this.orchestrator, db, enabled);

      this._useProxy.set(this.orchestrator.useProxy);
    }
  }

  /**
   * Toggle shared full internet access for shell/javascript tools.
   */
  async setVMBashFullInternetAccess(
    db: ShadowClawDatabase,
    enabled: boolean,
  ): Promise<void> {
    if (this.orchestrator) {
      await setVMBashFullInternetAccess(this.orchestrator, db, enabled);
      this._vmBashFullInternetAccess.set(
        this.orchestrator.vmBashFullInternetAccess,
      );
    }
  }

  /**
   * Switch active conversation
   */
  async switchConversation(
    db: ShadowClawDatabase,
    groupId: string,
    clearUnread?: boolean,
  ): Promise<void> {
    this.setActiveGroup(
      db,
      groupId,
      clearUnread ?? this._activePage.get() === "chat",
    );
    await setConfig(db, CONFIG_KEYS.LAST_ACTIVE_GROUP, groupId);
  }

  /**
   * Toggle a task
   */
  async toggleTask(
    db: ShadowClawDatabase,
    task: Task,
    enabled: boolean,
  ): Promise<void> {
    const updatedTask = { ...task, enabled };
    await saveTask(db, updatedTask);
    await this.loadTasks(db);

    const serverOk = await syncTaskToServer(
      updatedTask,
      this.getTaskServerBaseUrl(),
      await this.getSubscriberId(db),
      this.orchestrator?.taskServerEnabled ?? false,
    );
    if (!serverOk) {
      console.warn("Failed to update task on server — queued for replay.");

      await this.queueTaskSyncOutboxOperation(db, {
        type: "upsert",
        id: updatedTask.id,
        task: updatedTask,
        queuedAt: Date.now(),
      });
    }
  }

  /**
   * Update the pinned provider and model to an existing conversation
   */
  async updateConversationPinnedProvider(
    db: ShadowClawDatabase,
    groupId: string,
    providerId?: string,
    modelId?: string,
    pinnedMaxTokens?: number,
  ): Promise<void> {
    await updateGroupPinnedProvider(
      db,
      groupId,
      providerId,
      modelId,
      pinnedMaxTokens,
    );
    await this.loadGroups(db);
  }

  /**
   * Update provider module runtime overrides for a conversation.
   */
  async updateConversationProviderRuntimeOverrides(
    db: ShadowClawDatabase,
    groupId: string,
    overrides: GroupMeta["providerRuntimeOverrides"],
  ): Promise<void> {
    await updateGroupProviderRuntimeOverrides(db, groupId, overrides);
    await this.loadGroups(db);
  }

  /**
   * Update subagent provider/model selection policy for a conversation.
   */
  async updateConversationSubagentSettings(
    db: ShadowClawDatabase,
    groupId: string,
    mode: "automatic" | "manual",
    providerId?: string,
    modelId?: string,
    subagentMaxTokens?: number,
  ): Promise<void> {
    await updateGroupSubagentSettings(
      db,
      groupId,
      mode,
      providerId,
      modelId,
      subagentMaxTokens,
    );
    await this.loadGroups(db);
  }

  /**
   * Update the tool tags pinned to an existing conversation
   */
  async updateConversationToolTags(
    db: ShadowClawDatabase,
    groupId: string,
    tags: string[],
  ): Promise<void> {
    await updateGroupToolTags(db, groupId, tags);
    await this.loadGroups(db);
  }

  /**
   * Save/update a task locally, then sync or queue replay if server is unavailable.
   */
  async upsertTask(
    db: ShadowClawDatabase,
    task: Task,
    options?: { reloadTasks?: boolean },
  ): Promise<void> {
    const shouldReload = options?.reloadTasks !== false;

    await saveTask(db, task);
    if (shouldReload) {
      await this.loadTasks(db);
    }

    const subscriberId = await this.getSubscriberId(db);
    const serverOk = await syncTaskToServer(
      task,
      this.getTaskServerBaseUrl(),
      subscriberId,
      this.orchestrator?.taskServerEnabled ?? false,
    );
    if (!serverOk) {
      console.warn("Failed to sync task to server — queued for replay.");
      await this.queueTaskSyncOutboxOperation(db, {
        type: "upsert",
        id: task.id,
        task,
        queuedAt: Date.now(),
      });
    }
  }

  private compactTaskSyncOutbox(
    ops: TaskSyncOutboxOperation[],
  ): TaskSyncOutboxOperation[] {
    const lastIndexById = new Map<string, number>();
    ops.forEach((op, index) => {
      lastIndexById.set(op.id, index);
    });

    return ops.filter((op, index) => lastIndexById.get(op.id) === index);
  }

  private deriveGroupName(groupId: string): string {
    if (groupId.startsWith("tg:")) {
      return `Telegram ${groupId.slice(3)}`;
    }

    if (groupId.startsWith("im:")) {
      return `iMessage ${groupId.slice(3)}`;
    }

    if (groupId.startsWith("br:")) {
      return groupId === DEFAULT_GROUP_ID ? "Main" : "Browser Conversation";
    }

    return "Conversation";
  }

  private getActivityLogSessionStartedAt(groupId: string): string {
    const existing = this._activityLogSessionStartedAtByGroup.get(groupId);
    if (existing) {
      return existing;
    }

    const startedAt = new Date().toISOString();
    this._activityLogSessionStartedAtByGroup.set(groupId, startedAt);

    return startedAt;
  }

  // --- Getters for reactive state ---

  private getTaskServerBaseUrl(): string {
    return this.orchestrator?.taskServerUrl ?? "/schedule";
  }

  private async getSubscriberId(db: ShadowClawDatabase): Promise<string> {
    if (this._subscriberId) {
      return this._subscriberId;
    }

    const subscriberId = await getOrCreateSubscriberId(db);
    this._subscriberId = subscriberId;

    return subscriberId;
  }

  private normalizePagePath(path: string): string {
    const normalized = path.trim().replace(/^\/+/, "").replace(/\/+/g, "/");

    // Migrate legacy default page paths into the current default page.
    if (normalized === "main/memory.md") {
      return OrchestratorStore.DEFAULT_PAGE_PATH;
    }

    return normalized;
  }

  private normalizeSidebarDefaultPage(
    value: unknown,
  ): "pages" | "chat" | "tasks" | "files" {
    if (
      value === "pages" ||
      value === "chat" ||
      value === "tasks" ||
      value === "files"
    ) {
      return value;
    }

    return "chat";
  }

  private parsePagesList(raw: string | null | undefined): SavedPageRef[] {
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((entry) => {
          if (typeof entry === "string") {
            return {
              groupId: DEFAULT_GROUP_ID,
              path: this.normalizePagePath(entry),
            } satisfies SavedPageRef;
          }

          if (!entry || typeof entry !== "object") {
            return null;
          }

          const page = entry as Partial<SavedPageRef>;
          if (typeof page.path !== "string") {
            return null;
          }

          const normalizedGroupId =
            typeof page.groupId === "string" && page.groupId.trim().length > 0
              ? page.groupId
              : DEFAULT_GROUP_ID;

          const normalizedPath =
            normalizedGroupId === DEFAULT_GROUP_ID
              ? this.normalizePagePath(page.path)
              : page.path.trim();

          if (!normalizedPath) {
            return null;
          }

          return {
            groupId: normalizedGroupId,
            path: normalizedPath,
          } satisfies SavedPageRef;
        })
        .filter((entry): entry is SavedPageRef => !!entry)
        .filter((entry, index, arr) => {
          const key = `${entry.groupId}\u0000${entry.path}`;

          return (
            index ===
            arr.findIndex((candidate) => {
              return `${candidate.groupId}\u0000${candidate.path}` === key;
            })
          );
        });
    } catch {
      return [];
    }
  }

  private parseTaskSyncOutbox(raw: string | null | undefined) {
    if (!raw) {
      return [] as TaskSyncOutboxOperation[];
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [] as TaskSyncOutboxOperation[];
      }

      const normalized = parsed
        .filter((entry): entry is any => !!entry && typeof entry === "object")
        .filter((entry) => entry.type === "upsert" || entry.type === "delete")
        .filter((entry) => typeof entry.id === "string" && entry.id.length > 0)
        .map((entry) => ({
          ...entry,
          queuedAt:
            typeof entry.queuedAt === "number" ? entry.queuedAt : Date.now(),
        })) as TaskSyncOutboxOperation[];

      return this.compactTaskSyncOutbox(normalized);
    } catch {
      return [] as TaskSyncOutboxOperation[];
    }
  }

  private resolveSidebarDefaultPageForActivePage(
    page: string,
  ): "pages" | "chat" | "tasks" | "files" {
    if (
      page === "pages" ||
      page === "chat" ||
      page === "tasks" ||
      page === "files"
    ) {
      return page;
    }

    return this._sidebarDefaultPage.get();
  }

  private setPages(pages: SavedPageRef[]): void {
    const memoryPages = pages.filter(
      (p) => p.path.toLowerCase() === "memory.md",
    );
    const otherPages = pages.filter(
      (p) => p.path.toLowerCase() !== "memory.md",
    );
    this._pages.set([...otherPages, ...memoryPages]);
  }

  private async ensureDefaultPage(db: ShadowClawDatabase): Promise<void> {
    if (await isMainGroupMemorySuppressed(db)) {
      return;
    }

    const hasWorkspaceReadme = await ensureMainGroupMemory(
      db,
      DEFAULT_GROUP_ID,
    );

    await ensureMainGroupIndex(db, DEFAULT_GROUP_ID);

    if (!hasWorkspaceReadme || this._pages.get().length > 0) {
      return;
    }

    this.setPages([
      {
        groupId: DEFAULT_GROUP_ID,
        path: OrchestratorStore.DEFAULT_PAGE_PATH,
      },
    ]);
    await this.persistPages(db);
  }

  private async ensureGroupExists(
    db: ShadowClawDatabase,
    groupId: string,
    timestamp?: number,
  ): Promise<void> {
    const groups = this._groups.get();
    if (groups.some((g) => g.groupId === groupId)) {
      return;
    }

    const nextGroups = [
      ...groups,
      {
        groupId,
        name: this.deriveGroupName(groupId),
        createdAt: timestamp || Date.now(),
      },
    ];

    this._groups.set(nextGroups);

    try {
      await saveGroupMetadata(db, nextGroups);
    } catch (error) {
      console.error("Failed to persist new conversation metadata:", error);
    }
  }

  private async forwardActivityLogEntryToServer(
    entry: ThinkingLogEntry,
  ): Promise<void> {
    if (!this._db) {
      return;
    }

    const enabled = isConfigEnabled(
      await getConfig(this._db, CONFIG_KEYS.ACTIVITY_LOG_DISK_LOGGING_ENABLED),
    );

    if (!enabled) {
      return;
    }

    const groupId =
      typeof entry.groupId === "string" && entry.groupId
        ? entry.groupId
        : this._activeGroupId.get();

    if (!groupId) {
      return;
    }

    const message =
      typeof entry.message === "string" ? entry.message.trim() : "";
    if (!message) {
      return;
    }

    if (entry.level === "info" && entry.label === "Starting") {
      this._activityLogSessionStartedAtByGroup.set(
        groupId,
        new Date().toISOString(),
      );
    }

    const sessionStartedAt = this.getActivityLogSessionStartedAt(groupId);

    try {
      await fetch("/activity-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId,
          level: entry.level,
          label: entry.label,
          message,
          timestamp: new Date().toISOString(),
          sessionStartedAt,
        }),
      });
    } catch (error) {
      console.warn("Failed to persist activity log entry to server:", error);
    }
  }

  scheduleBackgroundStaticMainSiteSeeding(db: ShadowClawDatabase): void {
    if (typeof window === "undefined") {
      return;
    }

    const runBackgroundSeed = async () => {
      try {
        const isSeeded = await isStaticMainSiteSeeded(db, DEFAULT_GROUP_ID);
        if (isSeeded) {
          return;
        }

        const rawPagesList = await getConfig(db, CONFIG_KEYS.PAGES_LIST);
        const existingPages =
          rawPagesList !== null && rawPagesList !== undefined
            ? this.parsePagesList(rawPagesList)
            : [];

        const updated = await seedStaticMainSite(
          db,
          DEFAULT_GROUP_ID,
          existingPages,
          { preferEmbedded: false },
        );

        if (updated && updated.length > 0) {
          this.setPages(updated);
          await this.persistPages(db);
        }
      } catch (err) {
        console.warn("Background static main site seeding failed:", err);
      }
    };

    if (typeof (window as any).requestIdleCallback === "function") {
      (window as any).requestIdleCallback(() => void runBackgroundSeed(), {
        timeout: 3000,
      });
    } else {
      setTimeout(() => void runBackgroundSeed(), 100);
    }
  }

  private async persistPages(db: ShadowClawDatabase): Promise<void> {
    await setConfig(
      db,
      CONFIG_KEYS.PAGES_LIST,
      JSON.stringify(this._pages.get()),
    );
  }

  private async persistTaskSyncOutbox(db: ShadowClawDatabase): Promise<void> {
    await setConfig(
      db,
      CONFIG_KEYS.TASK_SYNC_OUTBOX,
      JSON.stringify(this._taskSyncOutbox),
    );
  }

  private async queueTaskSyncOutboxOperation(
    db: ShadowClawDatabase,
    op: TaskSyncOutboxOperation,
  ): Promise<void> {
    this._taskSyncOutbox = this.compactTaskSyncOutbox([
      ...this._taskSyncOutbox,
      op,
    ]);

    await this.persistTaskSyncOutbox(db);
  }

  private async applySiteConfigDefaults(db: ShadowClawDatabase): Promise<void> {
    if (typeof document === "undefined") {
      return;
    }

    try {
      const scriptEl = document.getElementById("shadow-claw-site-config");
      if (!scriptEl?.textContent) {
        return;
      }

      const config = JSON.parse(scriptEl.textContent);
      if (!config || typeof config !== "object") {
        return;
      }

      configureCustomElementSecurity(config);
      installCustomElementsRegistryGuard();
      installCustomElementDomGuard();

      const customElConfig = config.customElements;

      // Clear any erroneous group-level toolTags override on DEFAULT_GROUP_ID
      const groups = await listGroups(db);
      const defaultGroup = groups.find((g) => g.groupId === DEFAULT_GROUP_ID);
      if (defaultGroup?.toolTags) {
        await updateGroupToolTags(db, DEFAULT_GROUP_ID, undefined);
      }

      const isSeeded = await getConfig(db, CONFIG_KEYS.SITE_CONFIG_SEEDED);
      if (isSeeded) {
        return;
      }

      const allowedElements =
        config.allowedCustomElements ||
        (typeof customElConfig === "object" && !Array.isArray(customElConfig)
          ? customElConfig.allowedElements
          : undefined);
      if (allowedElements !== undefined) {
        await setConfig(
          db,
          CONFIG_KEYS.ALLOWED_CUSTOM_ELEMENTS,
          JSON.stringify(allowedElements),
        );
      }

      const allowedDomains =
        config.allowedCustomElementDomains ||
        (typeof customElConfig === "object" && !Array.isArray(customElConfig)
          ? customElConfig.allowedDomains
          : undefined);
      if (allowedDomains !== undefined) {
        await setConfig(
          db,
          CONFIG_KEYS.ALLOWED_CUSTOM_ELEMENT_HOST_PATTERNS,
          JSON.stringify(allowedDomains),
        );
      }

      if (config.sidebar && typeof config.sidebar === "object") {
        const {
          pagesHidden,
          chatHidden,
          tasksHidden,
          filesHidden,
          defaultPage,
        } = config.sidebar;
        if (pagesHidden !== undefined) {
          await setConfig(
            db,
            CONFIG_KEYS.SIDEBAR_PAGES_HIDDEN,
            String(Boolean(pagesHidden)),
          );
        }
        if (chatHidden !== undefined) {
          await setConfig(
            db,
            CONFIG_KEYS.SIDEBAR_CHAT_HIDDEN,
            String(Boolean(chatHidden)),
          );
        }
        if (tasksHidden !== undefined) {
          await setConfig(
            db,
            CONFIG_KEYS.SIDEBAR_TASKS_HIDDEN,
            String(Boolean(tasksHidden)),
          );
        }
        if (filesHidden !== undefined) {
          await setConfig(
            db,
            CONFIG_KEYS.SIDEBAR_FILES_HIDDEN,
            String(Boolean(filesHidden)),
          );
        }
        if (defaultPage && typeof defaultPage === "string") {
          await setConfig(db, CONFIG_KEYS.SIDEBAR_DEFAULT_PAGE, defaultPage);
        }
      }

      if (config.settings && typeof config.settings === "object") {
        const { assistantName } = config.settings;
        if (assistantName && typeof assistantName === "string") {
          await setConfig(db, CONFIG_KEYS.ASSISTANT_NAME, assistantName);
        }
      }

      const defaultToolsProfile =
        (config.settings && typeof config.settings === "object"
          ? config.settings.defaultToolsProfile
          : undefined) ?? config.defaultToolsProfile;
      if (
        typeof defaultToolsProfile === "string" &&
        defaultToolsProfile.trim().length > 0
      ) {
        const profileValue = defaultToolsProfile.trim();
        if (profileValue.toLowerCase() === "none") {
          await toolsStore.deactivateProfile(db);
        } else {
          const matchedProfile = toolsStore.profiles.find(
            (p) =>
              p.id.toLowerCase() === profileValue.toLowerCase() ||
              p.name.toLowerCase() === profileValue.toLowerCase(),
          );
          if (matchedProfile) {
            await toolsStore.activateProfile(db, matchedProfile.id);
          } else {
            console.warn(
              `Default tools profile "${profileValue}" specified in site-config was not found.`,
            );
          }
        }
      }

      if (config.pages && typeof config.pages === "object") {
        const { defaultPinnedPage } = config.pages;
        if (defaultPinnedPage && typeof defaultPinnedPage === "string") {
          await setConfig(
            db,
            CONFIG_KEYS.DEFAULT_PINNED_PAGE,
            defaultPinnedPage,
          );
        }
      }

      if (Array.isArray(config.enabledTools)) {
        const enabledTools = config.enabledTools.filter(
          (name: unknown): name is string =>
            typeof name === "string" && name.trim().length > 0,
        );
        await toolsStore.setAllEnabled(db, enabledTools);
        await toolsStore.setAllDeclarativeEnabled(db, enabledTools);
      }

      await setConfig(db, CONFIG_KEYS.SITE_CONFIG_SEEDED, "true");
    } catch (err) {
      console.warn("Failed to apply site-config defaults:", err);
    }
  }
}

export const orchestratorStore = new OrchestratorStore();
